import React, { useEffect, useMemo, useState } from 'react';
import { hrmsCalendarWeek } from '../../lib/hrmsApi';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ChevronLeft, ChevronRight, AlertTriangle, CalendarClock, Palmtree } from 'lucide-react';

const mondayOf = (d) => {
  const x = new Date(d);
  const day = x.getDay(); // 0 = Sun, 1 = Mon
  const diff = day === 0 ? -6 : 1 - day;
  x.setDate(x.getDate() + diff);
  return x.toISOString().slice(0, 10);
};

const fmtDay = (iso) => {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
};

const isToday = (iso) => iso === new Date().toISOString().slice(0, 10);

const CalendarPage = () => {
  const [start, setStart] = useState(mondayOf(new Date()));
  const [data, setData] = useState(null);

  const load = async () => setData(await hrmsCalendarWeek(start).catch(() => null));
  useEffect(() => { load(); }, [start]);

  const shift = (n) => {
    const d = new Date(start + 'T00:00:00'); d.setDate(d.getDate() + n * 7);
    setStart(d.toISOString().slice(0, 10));
  };

  const totalConflicts = useMemo(() => {
    if (!data) return 0;
    return Object.values(data.by_day || {}).reduce((s, blk) => s + (blk.conflicts?.length || 0), 0);
  }, [data]);

  return (
    <div data-testid="calendar-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold">Team calendar · Week view</h1>
          <p className="text-sm text-slate-500 mt-1">Interviews & approved leaves across the team. Conflicts are flagged automatically.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => shift(-1)} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <div className="font-mono text-sm text-slate-700">{start} → {data?.end || '…'}</div>
          <Button variant="outline" onClick={() => setStart(mondayOf(new Date()))} data-testid="cal-today">Today</Button>
          <Button variant="outline" onClick={() => shift(1)} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3 text-xs">
        <span className="inline-flex items-center gap-1 text-slate-600"><CalendarClock className="h-3.5 w-3.5 text-blue-600" /> {data?.total_interviews ?? 0} interviews</span>
        <span className="inline-flex items-center gap-1 text-slate-600"><Palmtree className="h-3.5 w-3.5 text-emerald-600" /> {data?.total_leaves ?? 0} on leave</span>
        {totalConflicts > 0 && <span className="inline-flex items-center gap-1 text-red-600 font-semibold" data-testid="cal-conflicts"><AlertTriangle className="h-3.5 w-3.5" /> {totalConflicts} conflict{totalConflicts > 1 ? 's' : ''}</span>}
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-7 gap-3">
        {(data?.days || []).map((d) => {
          const blk = data.by_day[d];
          const conflicted = (blk?.conflicts?.length || 0) > 0;
          return (
            <div key={d} className={`rounded-xl bg-white border p-3 min-h-[220px] ${conflicted ? 'border-red-300' : 'border-slate-200'}`} data-testid={`cal-day-${d}`}>
              <div className={`text-xs uppercase tracking-widest ${isToday(d) ? 'text-orange-600 font-bold' : 'text-slate-500'}`}>{fmtDay(d)}</div>
              {conflicted && (
                <div className="mt-2 rounded-md bg-red-50 border border-red-200 p-2 text-[11px] text-red-700">
                  <AlertTriangle className="h-3 w-3 inline mr-1" />
                  {blk.conflicts.map((c, i) => (
                    <div key={i}>
                      {c.type === 'double_booked' ? <>Double-booked: <b>{c.interviewers.join(', ')}</b></> : <>On leave: <b>{c.interviewers.join(', ')}</b></>}
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-2 space-y-2">
                {blk.interviews.map((iv) => (
                  <div key={iv.id} className="rounded-lg border border-slate-200 bg-blue-50/40 p-2 text-xs">
                    <div className="flex items-center gap-1 text-blue-800 font-semibold"><CalendarClock className="h-3 w-3" /> {iv.time_start}{iv.time_end ? `–${iv.time_end}` : ''}</div>
                    <div className="mt-0.5 font-medium text-slate-900">{iv.candidate_name}</div>
                    <div className="text-[11px] text-slate-500">{iv.job_title} · {iv.round_name}</div>
                    {iv.meet_link && <a href={iv.meet_link} target="_blank" rel="noreferrer" className="text-[10px] text-blue-600 hover:underline">Meet →</a>}
                  </div>
                ))}
                {blk.leaves.map((l) => (
                  <div key={l.id} className="rounded-lg border border-emerald-200 bg-emerald-50/70 p-2 text-xs">
                    <div className="flex items-center gap-1 text-emerald-800 font-semibold"><Palmtree className="h-3 w-3" /> On leave · {l.leave_type}</div>
                    <div className="mt-0.5 font-medium text-slate-900">{l.employee_name}</div>
                    <div className="text-[11px] text-slate-500">{l.from_date} → {l.to_date}</div>
                  </div>
                ))}
                {blk.interviews.length === 0 && blk.leaves.length === 0 && <div className="text-[11px] text-slate-400 text-center py-6">Nothing scheduled</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CalendarPage;
